import React, {Fragment} from 'react';

import momenttz from 'moment-timezone';
import styled from 'styled-components';
import {
  getProcedureTaskCore,
  ProcedureTaskCoreWithVersion,
  ProcessTaskCoreWithVersion,
} from 'sweet-taskinfo';

import {UserAvatar} from 'app/components/general/avatars';
import {ListMessage} from 'app/components/general/ListMessage';
import {useCurrentUserState} from 'app/current-user/api';
import {ProcedureStep} from 'app/procedures/components/ProcedureDetailPage/ProcedureStep';
import {ProcedureStepFields} from 'app/procedures/components/ProcedureStepFields';
import {getMemoizedProcedureOrphanSteps} from 'app/procedures/selectors';
import {getMemoizedProcessOrphanSteps} from 'app/process/selectors';
import {NextStepLinkItem} from 'app/steps/components/NextStepLinkItem';
import {StepLaneItem} from 'app/steps/components/StepLaneItem';
import {useTaskUpdate} from 'app/tasks/task-update-hook';
import {useScrollIntoActiveStep} from 'app/tasks/utils';
import {gt} from 'app/utils/i18n';
import {onlyUpdateForKeys} from 'app/utils/react-utils';

import {CheckableChoices} from './CheckableChoices';
import {MarkCompletedButton} from './MarkCompletedButton';
import {TaskAutomation} from './TaskAutomation';
import {StyledStickyDiv, TaskStepActionButtons} from './TaskStepActionButtons';

import {StepAssignmentAvatars} from '../StepAssignmentAvatars';

import type {
  AnyTaskStepType,
  DecisionStepChoiceType,
  TaskConcreteInstanceSerializedType,
  TaskInstanceSerializedType,
} from 'sweet-types';

interface StyledProcedureStepLiProps {
  blocked: boolean;
}

interface Props {
  taskCore: ProcedureTaskCoreWithVersion | ProcessTaskCoreWithVersion;
  isTaskBlocked?: boolean;
  warnIncomplete: boolean;
  setWarnIncomplete: (arg0: boolean) => unknown;
  toggleStepCollapse: (id: string, collapse: boolean | null) => void;
  collapsedSteps: Record<string, boolean>;
  shouldConfirmOnLeave: (arg0: string, arg1: boolean) => void;
  updateTaskInstance: (
    task: TaskInstanceSerializedType,
    changes: any
  ) => Promise<TaskConcreteInstanceSerializedType>;
}

const BaseTaskDetailStepsList = ({
  taskCore,
  isTaskBlocked,
  warnIncomplete,
  setWarnIncomplete,
  toggleStepCollapse,
  collapsedSteps,
  shouldConfirmOnLeave,
  updateTaskInstance,
}: Props) => {
  const {timezone} = useCurrentUserState().data!;
  const {updateTaskStep} = useTaskUpdate((args) =>
    updateTaskInstance(taskCore.task, args.update)
  );

  useScrollIntoActiveStep(taskCore);

  const taskid = taskCore.task.id;

  function updateTask(payload: any, stepId: string): Promise<any> {
    setWarnIncomplete(false);
    shouldConfirmOnLeave(`task-detail-${taskid}-${stepId}`, true);

    return updateTaskInstance(taskCore.task, payload).then((task) => {
      shouldConfirmOnLeave(`task-detail-${taskid}-${stepId}`, false);
      return task;
    });
  }

  const toggleStepCheck = (
    step: AnyTaskStepType,
    checked: boolean
  ): Promise<any> => {
    toggleStepCollapse(step.id, null);
    return updateTaskStep(taskCore, step, checked);
  };

  const toggleStepInput = (
    step: AnyTaskStepType,
    fieldId: string,
    value: string
  ): Promise<any> => {
    if (taskCore.ct === 'process') {
      return Promise.reject();
    } else {
      return updateTask(
        taskCore.payload.toUpdateStepField(step.id, fieldId, value),
        step.id
      );
    }
  };

  const toggleStepChoice = (
    step: AnyTaskStepType,
    choice: DecisionStepChoiceType,
    checked: boolean
  ): Promise<any> => {
    toggleStepCollapse(step.id, null);
    return updateTask(
      taskCore.ct === 'process'
        ? taskCore.payload.toSetDecisionStepCompleted(
            step.id,
            checked ? choice.id || null : null
          )
        : taskCore.payload.toSetStepCompleted(step.id, checked, choice.id),
      step.id
    );
  };

  const steps = taskCore.version.content;

  const version = taskCore.version;

  let orphans;

  if ('content_type' in version && version.content_type === 'processversion') {
    orphans = getMemoizedProcessOrphanSteps(
      version.content,
      taskCore.state.progressInfo.isSequenced
    );
  } else {
    orphans = getMemoizedProcedureOrphanSteps(
      version.content,
      taskCore.state.progressInfo.isSequenced
    );
  }

  return steps != null ? (
    <Fragment>
      <ol className="steps">
        {taskCore.state.contentWithProgress.map(
          ([_step, progress, stepAssignments], index) => {
            let step: AnyTaskStepType = {..._step};
            if (!step.type) {
              // I'm not sure we need this check here
              // I think content steps inside tasks are normalized and always have a type
              step.type = 'content';
            }

            const isCollapsed = collapsedSteps[step.id];
            const {isActive, automation, isCompleted, canToggle, completed} =
              progress;
            const showToggle: boolean =
              step.type === 'content' ||
              ((step.type === 'decision' || step.type === 'automation') &&
                isCompleted) ||
              step.type === 'processprocedure';

            const stepBlocked: boolean = !canToggle || isTaskBlocked === true;
            let isProcedureStepsBlocked = false;

            if (
              !isCompleted &&
              step.type === 'processprocedure' &&
              !step.deleted
            ) {
              const procedureTaskCore = getProcedureTaskCore(
                taskCore.task,
                step.id
              );
              if (procedureTaskCore.hasVersion === true) {
                const {canBeCompleted, isSequenced} =
                  procedureTaskCore.state.progressInfo;
                if (isSequenced && !canBeCompleted) {
                  isProcedureStepsBlocked = true;
                }
              }
            }

            return (
              <StyledProcedureStepLi
                id={step.id}
                key={index}
                blocked={stepBlocked}
              >
                <ProcedureStep
                  index={index}
                  step={step}
                  warnIncomplete={warnIncomplete && canToggle && !isCompleted}
                  orphan={orphans.has(step.id)}
                  collapsed={
                    isCollapsed != null ? isCollapsed : !isActive || isCompleted
                  }
                  contentType={'taskinstance'}
                  onToggleCollapse={toggleStepCollapse}
                  actionButtons={
                    showToggle ? (
                      <TaskStepActionButtons
                        isCompleted={isCompleted}
                        completedBy={completed != null ? completed.by : null}
                        completedAt={completed != null ? completed.at : null}
                        disabled={stepBlocked}
                        isProcedureStepsBlocked={isProcedureStepsBlocked}
                        userTimezone={timezone}
                        onToggleCheck={(checked) =>
                          toggleStepCheck(step, checked)
                        }
                      />
                    ) : step.type === 'ending' &&
                      isActive &&
                      index < steps.length - 1 ? (
                      <StyledStickyDiv noPadding={true}>
                        <MarkCompletedButton
                          taskCore={taskCore}
                          isTaskBlocked={isTaskBlocked}
                          updateTaskInstance={updateTaskInstance}
                          onBlockedClick={() => setWarnIncomplete(true)}
                          className="float-right mb-2"
                          small={true}
                        />
                      </StyledStickyDiv>
                    ) : null
                  }
                  fieldInputs={
                    step.type === 'content' ? (
                      <ProcedureStepFields
                        blocked={stepBlocked}
                        stepFields={step.fields}
                        fieldsProgress={progress.fields}
                        inputListener={(fieldId: string, value: any) =>
                          toggleStepInput(step, fieldId, value)
                        }
                      />
                    ) : null
                  }
                  choicesList={
                    step.type === 'decision' ? (
                      <CheckableChoices
                        disabled={stepBlocked}
                        onSetStepChoice={(choice, checked) =>
                          toggleStepChoice(step, choice, checked)
                        }
                        choices={step.choices}
                        steps={taskCore.version.content}
                        pickedChoice={completed?.pickedChoice ?? null}
                        userIndicator={
                          completed != null ? (
                            <UserAvatar
                              className="mr-3"
                              small={true}
                              user={completed.by}
                              tooltipTitle={(userName) => {
                                return gt(
                                  'Answer picked by %s on %s',
                                  <b>{userName}</b>,
                                  momenttz(completed.at)
                                    .tz(timezone)
                                    .format('ll')
                                );
                              }}
                            />
                          ) : null
                        }
                      />
                    ) : null
                  }
                  automation={
                    step.type === 'automation' ? (
                      <TaskAutomation
                        task={taskCore.task}
                        step={step}
                        automation={automation}
                        isCompleted={isCompleted}
                      />
                    ) : null
                  }
                  previous={
                    <>
                      <StepAssignmentAvatars
                        stepAssignments={stepAssignments}
                      />
                      <StepLaneItem
                        stepLaneId={
                          'lanes' in taskCore.version
                            ? taskCore.version.lanes[step.id]
                            : null
                        }
                      />
                    </>
                  }
                  next={<NextStepLinkItem step={step} steps={steps} />}
                />
              </StyledProcedureStepLi>
            );
          }
        )}
      </ol>
      {steps.length === 0 ? (
        <ListMessage gray={true}>{gt('No steps for this task')}</ListMessage>
      ) : (
        <div className="clearfix">
          <MarkCompletedButton
            taskCore={taskCore}
            isTaskBlocked={isTaskBlocked}
            updateTaskInstance={updateTaskInstance}
            onBlockedClick={() => setWarnIncomplete(true)}
            className="float-right"
          />
        </div>
      )}
    </Fragment>
  ) : null;
};

export const TaskDetailStepsList = onlyUpdateForKeys<Props>(
  BaseTaskDetailStepsList,
  ['taskSteps', 'collapsedSteps', 'warnIncomplete', 'taskCore', 'isTaskBlocked']
);

const StyledProcedureStepLi = styled.li`
  opacity: ${(props: StyledProcedureStepLiProps) =>
    props.blocked ? 0.4 : null};
`;
